import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import {
  KeyboardArrowLeft as KeyboardArrowLeftIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from "@mui/icons-material";
import { CalendarIcon } from "../../constants/icons";

function toMmDdYyyy(d) {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

function parseMmDdYyyy(s) {
  if (!s || typeof s !== "string") return null;
  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const month = Number(m[1]);
  const day = Number(m[2]);
  const year = Number(m[3]);
  if (!month || !day || !year) return null;
  const d = new Date(year, month - 1, day);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Calendar-style date range dialog (matches Alert History UI).
 * @param {{
 *  open: boolean;
 *  startDate: Date | null;
 *  endDate: Date | null;
 *  onCancel: () => void;
 *  onApply: (start: Date, end: Date) => void;
 * }} props
 */
export function CustomDateRangeDialog({
  open,
  startDate,
  endDate,
  onCancel,
  onApply,
}) {
  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);
  const [viewingMonth, setViewingMonth] = useState(new Date());
  const [tempStartStr, setTempStartStr] = useState("");
  const [tempEndStr, setTempEndStr] = useState("");

  useEffect(() => {
    if (!open) return;
    setTempStartDate(startDate || null);
    setTempEndDate(endDate || null);
    setTempStartStr(toMmDdYyyy(startDate));
    setTempEndStr(toMmDdYyyy(endDate));
    setViewingMonth(startDate || new Date());
  }, [open, startDate, endDate]);

  useEffect(() => {
    setTempStartStr(toMmDdYyyy(tempStartDate));
  }, [tempStartDate]);
  useEffect(() => {
    setTempEndStr(toMmDdYyyy(tempEndDate));
  }, [tempEndDate]);

  const canApply = Boolean(tempStartDate && tempEndDate);

  const calendarCells = useMemo(() => {
    const year = viewingMonth.getFullYear();
    const month = viewingMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDow = firstDay.getDay();

    const cells = [];
    for (let i = 0; i < startDow; i++) {
      cells.push(
        <Box
          key={`empty-${i}`}
          sx={{
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        />,
      );
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isToday = date.toDateString() === new Date().toDateString();
      const isStart =
        tempStartDate && date.toDateString() === tempStartDate.toDateString();
      const isEnd =
        tempEndDate && date.toDateString() === tempEndDate.toDateString();
      const isSelected = isStart || isEnd;
      const inRange =
        tempStartDate &&
        tempEndDate &&
        date >= tempStartDate &&
        date <= tempEndDate;

      cells.push(
        <Box
          key={`d-${day}`}
          onClick={() => {
            if (!tempStartDate || (tempStartDate && tempEndDate)) {
              setTempStartDate(date);
              setTempEndDate(null);
              return;
            }
            if (date < tempStartDate) {
              setTempEndDate(tempStartDate);
              setTempStartDate(date);
              return;
            }
            setTempEndDate(date);
          }}
          sx={{
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
            cursor: "pointer",
            fontFamily: "Roboto, sans-serif",
            fontSize: "14px",
            fontWeight: isSelected ? 600 : 400,
            color: isSelected ? "#FFFFFF" : isToday ? "#2932E5" : "#212121",
            backgroundColor: isSelected
              ? "#2932E5"
              : inRange
                ? "rgba(41, 50, 229, 0.1)"
                : isToday
                  ? "rgba(41, 50, 229, 0.08)"
                  : "transparent",
            "&:hover": {
              backgroundColor: isSelected
                ? "#2932E5"
                : "rgba(41, 50, 229, 0.1)",
            },
          }}
        >
          {day}
        </Box>,
      );
    }
    return cells;
  }, [viewingMonth, tempStartDate, tempEndDate]);

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          backgroundColor: "white",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          p: 2,
          pb: 1,
          backgroundColor: "white",
          borderTopLeftRadius: "12px",
          borderTopRightRadius: "12px",
        }}
      >
        <Typography
          sx={{
            lineHeight: "1.6",
            letterSpacing: "0.0075em",
            flex: "0 0 auto",
            fontFamily: "Roboto, sans-serif",
            fontSize: "20px",
            color: "rgb(255, 133, 85)",
            fontWeight: 600,
          }}
        >
          Custom Date Range
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: "10px", backgroundColor: "#F2F2F2" }}>
        <Box sx={{ display: "flex", gap: 2, mb: 3, mt: "10px" }}>
          <Box sx={{ flex: 1 }}>
            <TextField
              placeholder="mm/dd/yyyy"
              value={tempStartStr}
              onChange={(e) => {
                const v = e.target.value;
                setTempStartStr(v);
                if (v === "") {
                  setTempStartDate(null);
                  return;
                }
                const d = parseMmDdYyyy(v);
                if (d) {
                  setTempStartDate(d);
                  setViewingMonth(d);
                  if (tempEndDate && d > tempEndDate) setTempEndDate(null);
                }
              }}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CalendarIcon sx={{ color: "#4A5565" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                backgroundColor: "white",
                borderRadius: "8px",
                "& .MuiOutlinedInput-root": {
                  fontFamily: "Roboto, sans-serif",
                },
              }}
            />
            <Typography
              sx={{
                color: "rgba(0, 0, 0, 0.6)",
                fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 400,
                fontSize: "0.75rem",
                lineHeight: 1.66,
                letterSpacing: "0.03333em",
                textAlign: "left",
                marginTop: "5px",
                ml: 1.5,
              }}
            >
              Start Date
            </Typography>
          </Box>

          <Box sx={{ flex: 1 }}>
            <TextField
              placeholder="mm/dd/yyyy"
              value={tempEndStr}
              onChange={(e) => {
                const v = e.target.value;
                setTempEndStr(v);
                if (v === "") {
                  setTempEndDate(null);
                  return;
                }
                const d = parseMmDdYyyy(v);
                if (d) {
                  setTempEndDate(d);
                  if (tempStartDate && d < tempStartDate) {
                    setTempEndDate(tempStartDate);
                    setTempStartDate(d);
                  }
                }
              }}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CalendarIcon sx={{ color: "#4A5565" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                backgroundColor: "white",
                borderRadius: "8px",
                "& .MuiOutlinedInput-root": {
                  fontFamily: "Roboto, sans-serif",
                },
              }}
            />
            <Typography
              sx={{
                color: "rgba(0, 0, 0, 0.6)",
                fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 400,
                fontSize: "0.75rem",
                lineHeight: 1.66,
                letterSpacing: "0.03333em",
                textAlign: "left",
                margin: 0,
                ml: 1.5,
              }}
            >
              End Date
            </Typography>
          </Box>
        </Box>

        <Box sx={{ backgroundColor: "white", borderRadius: "12px", p: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
              pb: 1.5,
              borderBottom: "1px solid #E5E7EB",
            }}
          >
            <IconButton
              onClick={() => {
                const prev = new Date(viewingMonth);
                prev.setMonth(prev.getMonth() - 1);
                setViewingMonth(prev);
              }}
              sx={{
                backgroundColor: "white",
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                width: "36px",
                height: "36px",
                "&:hover": {
                  backgroundColor: "rgba(41, 50, 229, 0.08)",
                  borderColor: "#2932E5",
                },
              }}
            >
              <KeyboardArrowLeftIcon sx={{ color: "#4A5565" }} />
            </IconButton>

            <Typography
              sx={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "16px",
                fontWeight: 600,
                color: "#2932E5",
              }}
            >
              {viewingMonth.toLocaleDateString("en-US", {
                month: "long",
                year: "numeric",
              })}
            </Typography>

            <IconButton
              onClick={() => {
                const next = new Date(viewingMonth);
                next.setMonth(next.getMonth() + 1);
                setViewingMonth(next);
              }}
              sx={{
                backgroundColor: "white",
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                width: "36px",
                height: "36px",
                "&:hover": {
                  backgroundColor: "rgba(41, 50, 229, 0.08)",
                  borderColor: "#2932E5",
                },
              }}
            >
              <KeyboardArrowDownIcon
                sx={{ color: "#4A5565", transform: "rotate(-90deg)" }}
              />
            </IconButton>
          </Box>

          <Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: 0.5,
                mb: 1,
              }}
            >
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <Typography
                  key={day}
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#4A5565",
                    textAlign: "center",
                    py: 1,
                  }}
                >
                  {day}
                </Typography>
              ))}
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: 0.5,
              }}
            >
              {calendarCells}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2,
          pt: 2,
          backgroundColor: "white",
          borderBottomLeftRadius: "12px",
          borderBottomRightRadius: "12px",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            onClick={() => {
              setTempStartDate(null);
              setTempEndDate(null);
            }}
            sx={{
              textTransform: "none",
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              color: "#4A5565",
              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
            }}
          >
            Clear
          </Button>
          <Button
            onClick={() => {
              const t = new Date();
              setTempStartDate(t);
              setTempEndDate(t);
              setViewingMonth(t);
            }}
            sx={{
              textTransform: "none",
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              color: "#4A5565",
              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
            }}
          >
            Today
          </Button>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            onClick={onCancel}
            sx={{
              textTransform: "none",
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              color: "#4A5565",
              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (tempStartDate && tempEndDate) onApply(tempStartDate, tempEndDate);
            }}
            variant="contained"
            disabled={!canApply}
            sx={{
              textTransform: "none",
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              backgroundColor: "#2932E5",
              borderRadius: "8px",
              px: 3,
              "&:hover": { backgroundColor: "#1f28c7" },
              "&.Mui-disabled": {
                backgroundColor: "rgba(0, 0, 0, 0.12)",
                color: "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            Apply
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

